/**
 * The org's cases, each carrying its bookmarks and its attachments: one read shared by every
 * panel, so an edit in a case is seen by the video surface beside it.
 *
 * A write to a case shows at once and ends at the first load after its answer. A failed write
 * stays until it is sent again or discarded. A bookmark written anywhere reads the cases again
 * too (bookmarksAtoms), since the bookmarks in a case are the org's own.
 */

import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';

import { snapshotAtoms } from '../snapshotAtoms';
import { writesAtoms } from '../writesAtoms';
import { api } from '@/api';
import { getCaseWriteKeys, NEW_CASE_KEY } from '@/lib/caseWrites';
import {
    type CaseAttachment, type CaseDetail, type CasesSnapshot, type CaseWrite, CaseWriteKind,
    type CaseWriteRequest, type PendingCase,
} from '@/types/cases';
import type { Writes } from '@/types/writes';

interface AttachmentMove {
    attachmentId: string;
    position: number;
}

const PENDING: CasesSnapshot = { cases: [], isLoading: true, isLoaded: false, error: null };

function getCaseWithUpdates(record: CaseDetail, writes: CaseWrite[]): CaseDetail {
    let updatedRecord = record;
    for (const write of writes) {
        const isUpdateOfCase = write.kind === CaseWriteKind.Update && write.caseId === record.id;
        if (isUpdateOfCase) {
            updatedRecord = { ...updatedRecord, ...write.patch };
        }
    }
    return updatedRecord;
}

function getAttachmentWithUpdates(
    attachment: CaseAttachment,
    writes: CaseWrite[],
): CaseAttachment {
    let updatedAttachment = attachment;
    for (const write of writes) {
        const isUpdateOfAttachment = write.kind === CaseWriteKind.AttachmentUpdate &&
            write.attachmentId === attachment.id;
        if (isUpdateOfAttachment) {
            updatedAttachment = {
                ...updatedAttachment,
                name: write.patch.name ?? updatedAttachment.name,
                note: write.patch.note ?? updatedAttachment.note,
            };
        }
    }
    return updatedAttachment;
}

function getAttachmentMove(writes: CaseWrite[], caseId: string): AttachmentMove | undefined {
    let move: AttachmentMove | undefined;
    for (const write of writes) {
        const isUpdateInCase = write.kind === CaseWriteKind.AttachmentUpdate &&
            write.caseId === caseId;
        const position = isUpdateInCase ? write.patch.position : undefined;
        if (isUpdateInCase && position !== undefined) {
            move = { attachmentId: write.attachmentId, position };
        }
    }
    return move;
}

/** A move shows the attachment at its new place before the list shows the new order. */
function getAttachmentsInMoveOrder(
    attachments: CaseAttachment[],
    move?: AttachmentMove,
): CaseAttachment[] {
    const movedAttachment = attachments.find((attachment) => (
        attachment.id === move?.attachmentId));
    if (move === undefined || movedAttachment === undefined) {
        return attachments;
    }
    const persistedAttachments = attachments.filter((attachment) => (
        attachment.id !== movedAttachment.id));
    persistedAttachments.splice(move.position, 0, movedAttachment);
    return persistedAttachments;
}

function getCaseWithWrites(record: CaseDetail, writes: CaseWrite[]): CaseDetail {
    const attachments = record.attachments
        .map((attachment) => getAttachmentWithUpdates(attachment, writes));
    return {
        ...getCaseWithUpdates(record, writes),
        attachments: getAttachmentsInMoveOrder(attachments, getAttachmentMove(writes, record.id)),
    };
}

/** The listed cases with the writes of this session over them, failed writes too. */
export function getCasesWithWrites(
    cases: CaseDetail[],
    writes: Writes<CaseWriteRequest>,
): CaseDetail[] {
    const writesList = [...writes.values()];
    return cases.map((record) => getCaseWithWrites(record, writesList));
}

const snapshot = snapshotAtoms<CasesSnapshot>(
    PENDING,
    async () => {
        const cases = await api.listCases();
        return { cases, isLoading: false, isLoaded: true, error: null };
    },
    {
        onLoaded: (_snapshot, startedAtMs, get, set): void => {
            caseWrites.endAnswered(get, set, startedAtMs);
        },
    },
);

const caseWrites = writesAtoms<CaseWriteRequest>(snapshot.reload);

export const casesAtom = atom((get): CasesSnapshot => {
    const loadedSnapshot = get(snapshot.value);
    return {
        ...loadedSnapshot,
        cases: getCasesWithWrites(loadedSnapshot.cases, get(caseWrites.writes)),
    };
});

/** The case that was started here and that the list does not show yet. */
export const pendingCaseAtom = atom((get): PendingCase | undefined => {
    const createWrite = get(caseWrites.writes).get(NEW_CASE_KEY);
    return createWrite?.kind === CaseWriteKind.Create
        ? { title: createWrite.title, createWrite }
        : undefined;
});

/** The write under one key. It reads no list, so a reader requests no cases. */
export const caseWriteAtom = atomFamily((key: string) =>
    atom((get): CaseWrite | undefined => get(caseWrites.writes).get(key)));

export const dropCaseWriteAtom = atom(null, (_get, set, request: CaseWriteRequest): void => {
    getCaseWriteKeys(request).forEach((key) => set(caseWrites.drop, key));
});

/** A write replaces each older write that has one of its fields, so no two writes share one. */
export const startCaseWriteAtom = atom(null, (get, set, request: CaseWriteRequest): void => {
    const keys = getCaseWriteKeys(request);
    for (const key of keys) {
        const olderWrite = get(caseWrites.writes).get(key);
        if (olderWrite !== undefined) {
            set(dropCaseWriteAtom, olderWrite);
        }
    }
    keys.forEach((key) => set(caseWrites.start, key, request));
});

export const answerCaseWriteAtom = atom(null, (_get, set, request: CaseWriteRequest): void => {
    getCaseWriteKeys(request).forEach((key) => set(caseWrites.answer, key));
});

export const failCaseWriteAtom = atom(
    null,
    (_get, set, request: CaseWriteRequest, error: Error): void => {
        getCaseWriteKeys(request).forEach((key) => set(caseWrites.fail, key, error));
    },
);

/** Set this once a write to a case went through. */
export const refreshCasesAtom = snapshot.reload;
